import ComplementaryItem from '@components/balance/complementary-item/ComplementaryItem'
import SBIonIcon from '@components/balance/complementary-item/SBIonIcon'
import Program from '@components/membership/program/Program'
import Page from '@components/Page/Page'
import { TableFilters } from '@components/TableFilters'
import { TableUrlParams, useTableUrlSync } from '@hooks/useTableUrlSync'
import { ComplementaryItem as ComplementaryItemModel } from '@models/balance/ComplementaryItem'
import {
  getComplementaryItems,
  selectComplementaryItems,
  setComplementaryItemQuery,
} from '@store/balance/ComplementaryItemReducer'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { SBAsyncSelect } from '@supplement-bacon/react-async-select'
import { SBTable, TableParams } from '@supplement-bacon/sb-table-filters'
import { SBRM, SBRMAction, useSBRM } from '@supplement-bacon/sbrm-react'
import { Entity } from '@utils/Entities'
import { Permission } from '@utils/permissions'
import { Col, Row, Space, TableColumnsType, Tag, Typography } from 'antd'
import { useEffect } from 'react'

const { Text } = Typography

const ComplementaryItemsPage = () => {
  const { isOpen } = useSBRM()

  const dispatch = useAppDispatch()
  const complementaryItems = useAppSelector(selectComplementaryItems())
  const { query, isLoading } = useAppSelector(
    (state) => state.complementaryItem
  )

  const columns: TableColumnsType<ComplementaryItemModel> = [
    {
      title: 'Name',
      render: (record: ComplementaryItemModel) => (
        <ComplementaryItem.Avatar id={record.id} />
      ),
    },
    {
      title: 'Icon',
      render: (record: ComplementaryItemModel) =>
        record.icon ? (
          <SBIonIcon.Render name={record.icon} />
        ) : (
          <Text>No Icon</Text>
        ),
    },
    {
      title: 'Color',
      render: (record: ComplementaryItemModel) => (
        <Tag color={record.color}>{record.color || 'No Color'}</Tag>
      ),
    },
    {
      title: 'Programs',
      width: 200,
      render: (record: ComplementaryItemModel) => (
        <Space wrap>
          {record.programs?.map((id) => (
            <Program.Tag key={id} id={id} />
          ))}
        </Space>
      ),
    },
    {
      align: 'right',
      width: 100,
      render: (record: ComplementaryItemModel) => (
        <SBRM.Actions
          entity={Entity.complementaryItem}
          entityId={record.id}
          actions={[SBRMAction.Update, SBRMAction.Delete]}
        />
      ),
    },
  ]

  const fetchComplementaryItems = (params: TableUrlParams) => {
    const newQuery: TableParams = {
      ...query,
      pagination: {
        ...query.pagination,
        current: params.page,
        pageSize: params.pageSize,
      },
      filters: params.filters,
    }

    dispatch(setComplementaryItemQuery(newQuery))
    dispatch(getComplementaryItems(newQuery))
  }

  const { handleFiltersChange, handleTableChange, params } =
    useTableUrlSync<ComplementaryItemModel>({
      onParamsChange: fetchComplementaryItems,
    })

  useEffect(() => {
    if (isOpen) return

    fetchComplementaryItems(params)
  }, [isOpen])

  return (
    <Page
      permissions={Permission.manageComplementaryItems}
      pageTitle={{
        title: 'Complementary Items',
        level: 4,
        badge: { count: query.pagination?.total, overflowCount: 9999999 },
      }}
    >
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <TableFilters
            initialValues={{
              programs: params.filters.programs?.map(Number),
            }}
            onValuesChange={handleFiltersChange}
            left={[
              {
                name: 'programs',
                type: 'filter',
                label: 'Programs',
                input: (
                  <SBAsyncSelect
                    multiple
                    selectProps={{
                      maxTagCount: 'responsive',
                    }}
                    placeholder="Select programs"
                    entity={Entity.membershipProgram}
                    allowClear
                  />
                ),
              },
            ]}
            right={[
              {
                type: 'button',
                input: (
                  <SBRM.Actions
                    actions={[SBRMAction.Create]}
                    entity={Entity.complementaryItem}
                  />
                ),
              },
            ]}
          />
        </Col>
      </Row>
      <SBTable
        columns={columns}
        dataSource={complementaryItems}
        loading={isLoading}
        onChange={handleTableChange}
        pagination={query.pagination}
      />
    </Page>
  )
}

export default ComplementaryItemsPage
